<template>
  <er-card v-loading="loading">
    <layout-toolbar slot="header" type="flex" justify="space-between" align="center">
      <p class="card-title">{{ `${$t("Comn_pond")} ${$t("Comn_cumu_feed")}` }}</p>
      <el-col :span="10" :md="10" :lg="9" :xl="8">
        <er-date-picker
          v-model="dateRange"
          :disableDateMethod="disableDateMethod"
          :clearable="false"
          @change="handleValueChange"
        ></er-date-picker>
      </el-col>
    </layout-toolbar>
    <el-row :key="$i18n.locale">
      <high-charts :options="chartOptions" ref="highcharts" constructor-type="stockChart"></high-charts>
    </el-row>
  </er-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import chartMixin from "@/mixins/chartMixin";
import { pondScheduleGraphConfig } from "./chartOptions";

export default {
  mixins: [errorHandlerMixin, chartMixin],
  data: function() {
    return {
      loading: false,
      dateRange: [],
      params: {
        from_date: "",
        to_date: "",
        month_of: null,
        week_of: null,
        only_feed_meta_data: true
      },
      chartOptions: pondScheduleGraphConfig,
      pondSlotFeedLogs: []
    };
  },
  computed: {
    ...mapGetters("pondDetails", {
      getPondScheduleGraphSlotWiseFeed: "getPondScheduleGraphSlotWiseFeed"
    }),
    defaultMomentDtRangeForCurrCulture() {
      return this.getMomentObjArrOfCultureDateRange;
    }
  },
  methods: {
    ...mapActions("pondDetails", {
      fetchSlotWiseFeed: "fetchSlotWiseFeed"
    }),
    async initComponent(pondId) {
      this.loading = true;
      try {
        await this.fetchSlotWiseFeed({
          deviceType: "pond",
          params: this.params
        });
        this.pondSlotFeedLogs = this.getPondScheduleGraphSlotWiseFeed;
        this.initAxisTextKeys("Comn_date", "Comn_feed_kg");
        this.initChartLang();
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    generateData(
      feedLimit,
      dispensedLimit,
      totalFeedLimit,
      totalDispensedLimit
    ) {
      const startDate = this.$moment(this.dateRange[0]);
      const endDate = this.$moment(this.dateRange[0]);

      for (let i = 0; i < 10000; i++) {
        feedLimit.push({
          x: startDate.add(i, "seconds").toDate(),
          y: Math.random() * 1000
        });
        dispensedLimit.push({
          x: endDate.add(i, "seconds").toDate(),
          y: Math.random() * 1000
        });
      }
      totalFeedLimit = Object.values(feedLimit)
        .map(feed => feed.y)
        .reduce((acc, curr) => acc + curr);
      totalDispensedLimit = Object.values(dispensedLimit)
        .map(feed => feed.y)
        .reduce((acc, curr) => acc + curr);
      return { feedLimit, dispensedLimit, totalFeedLimit, totalDispensedLimit };
    },
    pondScheduleWiseFeed(pondFeedDaysWise, dateRange) {
      const dateScheduleWiseFeed = {};
      const dateScheduleWiseDispFeed = {};
      const dayStart = moment(dateRange[0] + "T00:00:00.000Z");
      const dayEnd = moment(dateRange[1] + "T00:00:00.000Z");
      while (dayStart <= dayEnd) {
        dateScheduleWiseFeed[dayStart.valueOf()] = 0;
        dateScheduleWiseDispFeed[dayStart.valueOf()] = 0;
        dayStart.add(1, "days");
      }
      pondFeedDaysWise.forEach(dayFeed => {
        const date = moment(dayFeed.date);
        dateScheduleWiseFeed[date.valueOf()] = Number(dayFeed.feed.toFixed(2));
        dateScheduleWiseDispFeed[date.valueOf()] = Number(
          dayFeed.dispensed_feed.toFixed(2)
        );
      });
      let scheduleFeed = Object.keys(dateScheduleWiseFeed).map(key => {
        return {
          x: Number(key),
          y: dateScheduleWiseFeed[key]
        };
      });
      let scheduleDispensedFeed = Object.keys(dateScheduleWiseDispFeed).map(
        key => {
          return {
            x: Number(key),
            y: dateScheduleWiseDispFeed[key]
          };
        }
      );
      scheduleFeed = scheduleFeed.sort((a, b) => a.x - b.x);
      scheduleDispensedFeed = scheduleDispensedFeed.sort((a, b) => a.x - b.x);
      return { scheduleFeed, scheduleDispensedFeed };
    },
    initChart() {
      let result = {};
      let feedLimit = [];
      let dispensedLimit = [];
      let totalFeedLimit = 0;
      let totalDispensedLimit = 0;
      if (this.pondSlotFeedLogs.length === 0) {
        this.chartOptions.series = [];
        return;
      } else {
        result = this.pondScheduleWiseFeed(this.pondSlotFeedLogs, [
          this.params.from_date,
          this.params.to_date
        ]);
        feedLimit = result.scheduleFeed;
        dispensedLimit = result.scheduleDispensedFeed;
        totalFeedLimit = Object.values(feedLimit)
          .map(feed => feed.y)
          .reduce((acc, curr) => acc + curr);
        totalDispensedLimit = Object.values(dispensedLimit)
          .map(feed => feed.y)
          .reduce((acc, curr) => acc + curr);
      }
      this.initChartSeries(
        feedLimit,
        dispensedLimit,
        totalFeedLimit,
        totalDispensedLimit
      );
    },
    initChartSeries(
      feedLimit,
      dispensedLimit,
      totalFeedLimit,
      totalDispensedLimit
    ) {
      this.chartOptions.series = [];
      this.chartOptions.series.push({
        name: {
          legend: `${this.$t("PM_day_feed")} (${this.$t(
            "Comn_total"
          )}: ${totalFeedLimit.toFixed(2)})`,
          tooltip: this.$t("PM_day_feed")
        },
        data: feedLimit,
        type: "area",
        color: "#7cb5ec",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(124, 181, 236,1)"],
            [1, "rgba(255,255,255,.25)"]
          ]
        },
        visible: false
      });
      this.chartOptions.series.push({
        name: {
          legend: `${this.$t("PM_dispensed")} (${this.$t(
            "Comn_total"
          )}: ${totalDispensedLimit.toFixed(2)})`,
          tooltip: this.$t("PM_dispensed")
        },
        data: dispensedLimit,
        type: "area",
        color: "#f7a35c",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(247, 163, 92,1)"],
            [1, "rgba(255,255,255,.25)"]
          ]
        },
        visible: true
      });
    },
    async handleValueChange(currentSelectedValues) {
      let from_date = this.$moment(currentSelectedValues[0]);
      // .utcOffset(
      //   this.calculatedTimezone
      // );
      let to_date = this.$moment(currentSelectedValues[1]);
      // .utcOffset(
      //   this.calculatedTimezone
      // );
      if (!this.checkDateWithInCultureRange(from_date)) {
        from_date = this.getMomentObjArrOfCultureDateRange[0];
      }
      if (!this.checkDateWithInCultureRange(to_date)) {
        to_date = this.getMomentObjArrOfCultureDateRange[1];
      }
      this.params.from_date = from_date.format("YYYY-MM-DD");
      this.params.to_date = to_date.format("YYYY-MM-DD");
      await this.initComponent(this.pondId);
    }
  }
};
</script>
<style lang="scss"></style>
